import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  GetCompatibleDocument,
  GetCompatibleQuery,
  GetCompatibleQueryVariables,
  SymbolDetailInfo,
  useApplySymbolMutation,
  useConfirmApplySymbolMutation,
  useGetDateTimeQuery,
  useGetSymbolInfoQuery,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import * as Yup from 'yup';
import { calcInterest } from '../calcInterest';
import { DateFormatType, formatDate } from 'views/utils/converter';
import { Client, Context as UrqlContext } from 'urql';
import { ApplyAssetType, ApplySymbolRequestType, findIntroductionConfirmationId } from 'views/utils/enums';
import { formErrorScroll } from 'views/mutual/formErrorScroll';

export enum SymbolPageStep {
  Detail,
  CompatibilityCheck,
  Confirm,
  Complete,
  ContractComplete,
}

export type ApplySymbolInput = {
  symbol: string;
  amount: number | null;
  clientRequestId: null;
  transferDate: Date | null;
  isContractDocumentOpened: boolean; // 契約締結前交付書面を開いたかどうか
  isContractDocumentCheck: boolean;
  isCheck2: boolean;
  isCheck3: boolean;
  isCheck4: boolean;
  isCheck5: boolean;
  isCheck6: boolean;
  isCheck7: boolean;
  isCheck8: boolean;
  // isCheck9: boolean;
  introductionConfirmation: string;
  applicationStartDateTime: string;
};

const transferDateRangeError = (transferStartDate: string, transferEndDate: string) => {
  return `${transferStartDate}～${transferEndDate}の範囲にしてください`;
};

const transferDateSchema = (
  transferStartDate: string | null | undefined,
  transferEndDate: string | null | undefined,
) => {
  // 入力制御しているが念のため
  const base = Yup.date().typeError('日付で入力してください').required('必須です');
  if (transferStartDate && transferEndDate) {
    return base
      .min(new Date(transferStartDate), transferDateRangeError(transferStartDate, transferEndDate))
      .max(new Date(transferEndDate), transferDateRangeError(transferStartDate, transferEndDate));
  }

  return base;
};

const applySymbolInputValidation = (
  transferStartDate: string | null | undefined,
  transferEndDate: string | null | undefined,
) =>
  Yup.object().shape({
    symbol: Yup.string().required(),
    amount: Yup.number()
      .typeError('半角数字で入力してください')
      .required('必須です')
      .min(1, '1以上で入力してください')
      .positive('1以上で入力してください')
      .integer('整数で入力してください'),
    transferDate: transferDateSchema(transferStartDate, transferEndDate),
    isContractDocumentOpened: Yup.boolean().isTrue(),
    isContractDocumentCheck: Yup.boolean().isTrue(),
    isCheck2: Yup.boolean().isTrue(),
    isCheck3: Yup.boolean().isTrue(),
    isCheck4: Yup.boolean().isTrue(),
    isCheck5: Yup.boolean().isTrue(),
    isCheck6: Yup.boolean().isTrue(),
    isCheck7: Yup.boolean().isTrue(),
    isCheck8: Yup.boolean().isTrue(),
    // 非表示中のためコメントアウト
    // isCheck9: Yup.boolean().isTrue(),
    // introductionConfirmation: Yup.string().required(ErrorWord.SELECT),
  });

// falseの場合は条件を満たしていないので申し込めない状態
const canApplySymbol = (symbol: SymbolDetailInfo | null | undefined, input: ApplySymbolInput): boolean => {
  if (
    symbol &&
    // apiのレスポンスとして申し込めるかのフラグがある
    symbol.Tradable &&
    input.isContractDocumentCheck &&
    input.isCheck2 &&
    input.isCheck3 &&
    input.isCheck4 &&
    input.isCheck5 &&
    input.isCheck6 &&
    input.isCheck7 &&
    input.isCheck8 //&&
    // input.isCheck9
  ) {
    return true;
  } else {
    return false;
  }
};

type SymbolPageProps = {
  symbol: string;
};

export const useLeaseFundSymbolPage = ({ symbol }: SymbolPageProps) => {
  const [symbolPageStep, setSymbolPageStep] = useState<SymbolPageStep>(SymbolPageStep.Detail);
  const [symbolDetailInfo, _reload] = useGetSymbolInfoQuery({
    variables: {
      symbol,
    },
    requestPolicy: 'network-only',
  });
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });

  const [applySymbolRes, applySymbol] = useApplySymbolMutation();
  const [confirmApplySymbolRes, confirmApplySymbol] = useConfirmApplySymbolMutation();
  const { showError, messageClose } = useGlobalToastHandler();
  const applyOnlyCodeList = [409, 411, 416, 417, 432];

  const client = useContext(UrqlContext) as Client;

  const onSubmit = useOnceSubmit<ApplySymbolInput>(
    {
      onPreviousSubmit: async (input) => {
        return canApplySymbol(symbolDetailInfo.data?.GetSymbolInfo, input);
      },
      onSubmit: async (input) => {
        switch (symbolPageStep) {
          case SymbolPageStep.Detail: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();
            getDataTime();

            // 適合性チェックが済んでいない場合は適合性チェックを表示する
            const compatibleRes = await client.query<GetCompatibleQuery, GetCompatibleQueryVariables>(
              GetCompatibleDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );

            if (compatibleRes.data?.GetAccountInfo?.Compatible?.LeaseFund) {
              const res = await confirmApplySymbol({
                input: {
                  Symbol: input.symbol!,
                  Amount: input.amount!,
                  TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                  IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                  RequestType: ApplySymbolRequestType.Confirm,
                  AssetType: ApplyAssetType.LeaseFund,
                  ApplicationStartDate: input.applicationStartDateTime,
                },
              });

              if (!res?.data) return;
              if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
                //undefinedの時はfalseになるように-1 を入れる
                setSymbolPageStep(SymbolPageStep.Confirm);
              } else {
                showError(res.data.ApplySymbol?.Message);
              }
              break;
            } else {
              setSymbolPageStep(SymbolPageStep.CompatibilityCheck);
            }

            break;
          }
          case SymbolPageStep.CompatibilityCheck: {
            const res = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.LeaseFund,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });
            if (!res?.data) return;
            if (res.data.ApplySymbol?.Code === 0 || applyOnlyCodeList.includes(res.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              setSymbolPageStep(SymbolPageStep.Detail);
              showError(res.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Confirm: {
            const appSymbolRes = await applySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                TransferDate: formatDate(input.transferDate, DateFormatType.Slash_yyyyMMdd),
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Apply,
                AssetType: ApplyAssetType.LeaseFund,
                ApplicationStartDate: input.applicationStartDateTime,
                ClientRequestId: input.clientRequestId!,
              },
            });
            if (!appSymbolRes?.data) return;
            if (appSymbolRes.data.ApplySymbol?.Code === 0) {
              setSymbolPageStep(SymbolPageStep.ContractComplete);
            } else if (applyOnlyCodeList.includes(appSymbolRes.data.ApplySymbol?.Code ?? -1)) {
              //undefinedの時はfalseになるように-1 を入れる
              setSymbolPageStep(SymbolPageStep.Complete);
            } else {
              showError(appSymbolRes.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Complete:
          case SymbolPageStep.ContractComplete:
            break;
        }
      },
    },
    [symbolPageStep, setSymbolPageStep, applySymbol, showError, messageClose],
  );

  const backDetailPage = useCallback(() => {
    switch (symbolPageStep) {
      case SymbolPageStep.Detail: {
        break;
      }
      case SymbolPageStep.Confirm: {
        setSymbolPageStep(SymbolPageStep.Detail);
        break;
      }
      case SymbolPageStep.Complete:
        break;
    }
  }, [symbolPageStep, setSymbolPageStep]);

  const formik = useFormik<ApplySymbolInput>({
    initialValues: {
      symbol,
      amount: null,
      clientRequestId: null,
      transferDate: null,
      isContractDocumentOpened: false,
      isContractDocumentCheck: false,
      isCheck2: false,
      isCheck3: false,
      isCheck4: false,
      isCheck5: false,
      isCheck6: false,
      isCheck7: false,
      isCheck8: false,
      // isCheck9: false,
      introductionConfirmation: '',
      applicationStartDateTime: '',
    },
    onSubmit,
    validationSchema: applySymbolInputValidation(
      symbolDetailInfo.data?.GetSymbolInfo?.TransferStartDate,
      symbolDetailInfo.data?.GetSymbolInfo?.TransferEndDate,
    ),
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.applicationStartDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  // formik.values.amountはx百万円 amountRawはx円
  const amounts = useMemo<{ interest: number | null; amountRaw: number | null; totalAmountRaw: number | null }>(() => {
    if (!formik.values.amount) {
      return {
        interest: null,
        amountRaw: null,
        totalAmountRaw: null,
      };
    }

    const amountRaw = formik.values.amount;

    if (formik.values.transferDate && symbolDetailInfo.data?.GetSymbolInfo) {
      const symbol = symbolDetailInfo.data?.GetSymbolInfo;
      const interest = calcInterest(
        amountRaw,
        formik.values.transferDate!,
        new Date(symbol.LeaseStartDate),
        symbol.TransferInterestPattern,
        symbol.TransferInterest,
        symbol.BoundaryAmount!,
        symbol.LowerRate!,
        symbol.UpperRate!,
      );

      return {
        interest: interest,
        amountRaw,
        totalAmountRaw: amountRaw + interest,
      };
    } else {
      return {
        interest: null,
        amountRaw: null,
        totalAmountRaw: null,
      };
    }
  }, [symbolDetailInfo.data?.GetSymbolInfo, formik.values.amount, formik.values.transferDate]);

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    symbolPageStep,
    symbolDetailInfo,
    applySymbolRes,
    confirmApplySymbolRes,
    formik,
    canApplySymbol: canApplySymbol(symbolDetailInfo.data?.GetSymbolInfo, formik.values),
    interest: amounts.interest,
    amountRaw: amounts.amountRaw,
    totalAmountRaw: amounts.totalAmountRaw,
    backDetailPage,
  };
};
